import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SessionService } from '@app/shared/services/session.service';
import { RetailersService, RetailerResponse, SaleResponse, SalesService, DistributionChannelResponse, CustomersService, SaleRequest, UserResponse, UsersService, UnregisteredUserResponse, UnregisteredUsersService, CustomerResponse, DistributionChannelsService, FilterRequestForCombo } from '@src/app/shared/backend/clients';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { Features } from '@src/app/shared/backend/auth/Feature';
import { BaseViewComponent } from '@src/app/shared/components/base-list/base-view.component';
import { ComboData } from '@src/app/shared/components/comboData/comboData';
import { latinCharsetRegexValidator } from '@src/app/shared/validators/latinCharacters.validator';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
@Component({
    selector: 'app-edit-sale',
    templateUrl: './edit-sale.component.html',
    styleUrl: './edit-sale.component.scss',
    host: {
        class: 'app-edit-sale'
    },
    standalone: false
})
export class EditSaleComponent extends BaseViewComponent implements OnInit {

  private readonly destroyedComponent$ = new Subject<void>();
  private isSyncingDropdowns = false; // Flag top prevent retriggering reactive form subscriptions

  private initialSystemUserOption: any | null = null;
  private initialGuestUserOption: any | null = null;
  private initialRetailerOption: any | null = null;
  private initialDistributionChannelOption: any | null = null;
  private initialSellerType: 'myself' | 'system' | 'guest' | null = null;

  private initialLoadCurrentOptionText: string = '';

  showIsLegacyField: boolean = false;

  saleId: number = 0;
  sale: SaleResponse = {};
  currentDate: Date = new Date();
  filterOperator = FilterOperator;

  showErrorSystemNoRetailersAssigned: boolean = false;
  showErrorGuestNoRetailersAssigned: boolean = false;
  showDistributionChannel: boolean = false;

  combosData: { [key: string]: ComboData } = {
    myselfRetailers: new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    myselfDistributionChannels: new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),

    systemUsers: new ComboData(async (filter) => await firstValueFrom(this.usersService.apiUsersFilterComboPost(filter))),
    systemRetailers: new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    systemDistributionChannels: new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter))),

    guestUsers: new ComboData(async (filter) => await firstValueFrom(this.sellersService.apiUnregisteredUsersFilterComboPost(filter))),
    guestRetailers: new ComboData(async (filter) => await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter))),
    guestDistributionChannels: new ComboData(async (filter) => await firstValueFrom(this.distributionChannelsService.apiDistributionChannelsFilterComboPost(filter)))
  };

  isSystemSeller: any = '';
  isGuestSeller: any = '';

  store: RetailerResponse = {};
  storesList: any;
  storesTotalRecords: any;
  storesRowsPerPageOption: number = 10;

  unregisteredUserName: any;

  user: UserResponse = {};
  usersList: any;
  usersTotalRecords: any;
  usersRowsPerPageOption: number = 10;

  seller: UnregisteredUserResponse = {};
  sellersList: any;
  sellersTotalRecords: any;
  sellersRowsPerPageOption: number = 10;

  customer: CustomerResponse = {};
  customersList: any[] = [];
  customersTotalRecords: number = 0;
  customersRowsPerPageOption: number = 10;

  hasUnregisteredUserFeature: boolean = false;
  showCustomer: boolean = false;
  canModifyPrice: boolean = false;
  canViewPrice: boolean = false;
  canAssignCustomer: boolean = false;

  distributionChannel: DistributionChannelResponse = {};
  distributionChannelsList: any;
  distributionChannelsTotalRecords: any;
  distributionChannelsRowsPerPageOption: number = 10;

  disabledCamera = true;
  canUseMyselfSeller = true;

  private debounceTimer: any;

  public saleForm: FormGroup = this.fb.group({
    itemId: ['', [Validators.required, Validators.maxLength(50)]],
    serialNumber: [null, [Validators.required]],
    item: [null],
    distributionChannel: [null],
    isLegacy: ['false'],
    isGift: ['false'],
    price: ['', [Validators.pattern(/^[0-9]*\.?[0-9]*$/)]],
    date: [this.currentDate, [Validators.required]],
    customer: [null],
    customerId: [[]],
    retailerId: [''],
    retailer: [null, [Validators.required]],
    userId: [''],
    user: [null],
    unregisteredUserId: [''],
    unregisteredUser: [null],
    sellerType: ['myself', [Validators.required]],
    myselfRetailerId: [[], [Validators.required]],
    myselfRetailer: [null],
    myselfDistributionChannelId: [[]],
    myselfDistributionChannel: [null],
    systemUserId: [[]],
    systemUser: [null],
    systemRetailerId: [[]],
    systemRetailer: [null],
    systemDistributionChannelId: [[]],
    systemDistributionChannel: [null],
    createGuestUser: [false],
    guestUserId: [[]],
    guestUser: [null],
    guestRetailerId: [[]],
    guestRetailer: [null],
    guestDistributionChannelId: [[]],
    guestDistributionChannel: [null],
    firstname: ['', [Validators.maxLength(50)]],
    lastname: ['', [Validators.maxLength(50)]],
    romanisedFirstName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    romanisedLastName: ['', [latinCharsetRegexValidator(), Validators.maxLength(50)]],
    changeSeller: [false]
  });

  constructor(
    public session: SessionService,
    public permissionService: PermissionService,
    private translate: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
    private retailersService: RetailersService,
    private customersService: CustomersService,
    private salesService: SalesService,
    private usersService: UsersService,
    private distributionChannelsService: DistributionChannelsService,
    private sellersService: UnregisteredUsersService,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit() {
    this.init();
  }

  private async init(): Promise<void> {
    this.initialLoadCurrentOptionText = this.translate.instant('DROPDOWN.CURRENT_VALUE');
    this.updateCanUseMyselfSeller();

    this.bindDropdownValueChanges();

    this.translate.onLangChange
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe((_event: LangChangeEvent) => {
        this.initialLoadCurrentOptionText = this.translate.instant('DROPDOWN.CURRENT_VALUE');
        this.setInitialSellerOptions();
        this.refreshInitialCurrentOptionsInLoadedCombos();
      });

    this.session.currentCompanyData$
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(company => {
        this.showIsLegacyField = company?.showIsLegacy ?? false;
        this.canViewPrice = company?.hasPrice ?? false;
      });

    this.hasUnregisteredUserFeature = await this.permissionService.companyHasFeaturePermissions(Features.Sellers);
    this.showCustomer = await this.permissionService.hasFeaturePermissions(Features.Customers);
    this.showDistributionChannel = await this.permissionService.hasFeaturePermissions(Features.DistributionChannels);

    this.route.params
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(async params => {
        try {
          const id = Number(params['saleId']);
          if (!id) {
            return;
          }

          this.saleId = id;
          this.sale = await firstValueFrom(this.salesService.apiSalesIdGet(id));
          this.setInitialSellerOptions();
          this.setInitialSellerType();

          await this.initForm();

          await this.getCanModifyPrice();
          await this.getCanAssignCustomer();
          this.updateCustomerControlState();

          if (this.showCustomer && this.canAssignCustomer) {
            await this.getCustomers({
              pageIndex: 1,
              pageSize: this.customersRowsPerPageOption,
              search: ''
            });
          }

          const selectedCustomer = await this.getSaleCustomer();
          this.ensureCustomerOption(selectedCustomer);

          this.saleForm.patchValue({
            customerId: this.sale?.customerId ? [this.sale.customerId] : []
          }, { emitEvent: false });
        } catch (error) {
          console.error('Edit sale init failed:', error);
        }
      });

    this.saleForm.get('changeSeller')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (value === false) {
          this.saleForm.get('sellerType')?.setValue('guest');
          this.setGuestSeller();
        }
      });
  }

  async onLazyLoadComboData(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' | 'guestUsers' | 'guestRetailers' | 'guestDistributionChannels',
    event: any
  ) {
    const search = event?.search?.trim() || null;
    const activePage = Math.max(1, (event?.pageIndex ?? 1));

    await this.combosData[comboType].getData(search, activePage);
    this.prependInitialOptionForCombo(comboType);
  }

  private refreshInitialCurrentOptionsInLoadedCombos(): void {
    this.prependInitialOptionForCombo('myselfRetailers');
    this.prependInitialOptionForCombo('myselfDistributionChannels');

    this.prependInitialOptionForCombo('systemUsers');
    this.prependInitialOptionForCombo('systemRetailers');
    this.prependInitialOptionForCombo('systemDistributionChannels');

    this.prependInitialOptionForCombo('guestUsers');
    this.prependInitialOptionForCombo('guestRetailers');
    this.prependInitialOptionForCombo('guestDistributionChannels');
  }

  getTotalPages(totalRecords: number, pageSize: number): number {
    return Math.max(1, Math.ceil((totalRecords ?? 0) / (pageSize || 1)));
  }

  private getSelectedId(value: any): number | string | null {
    if (!Array.isArray(value) || value.length === 0) {
      return null;
    }

    // unwrap the selected id from the form val (used by custom dropdown)
    return value[0];
  }

  private findComboOption(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels',
    id: number | string | null
  ): any | null {
    if (id == null) {
      return null;
    }

    return (this.combosData[comboType].list ?? []).find((option: any) => String(option?.id) === String(id)) ?? null;
  }

  private ensureComboOption(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |
      'guestUsers' | 'guestRetailers' | 'guestDistributionChannels',
    option: any | null
  ) {

    // ensure a selected option exists in the current list to display preselected value
    if (!option?.id) {
      return;
    }

    const list = this.combosData[comboType].list ?? [];
    const exists = list.some((x: any) => String(x?.id) === String(option.id));

    if (!exists) {
      this.combosData[comboType].list = [option, ...list];
      return;
    }

    this.combosData[comboType].list = list.map((x: any) =>
      String(x?.id) === String(option.id) ? { ...x, ...option } : x
    );
  }

  private prependComboOption(comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' | 'guestUsers' | 'guestRetailers' | 'guestDistributionChannels', option: any | null): void {
    if (!option?.id) {
      return;
    }

    // prepends option to the combo and remove duplicate
    const list = this.combosData[comboType].list ?? [];
    const listWithoutCurrentOption = list.filter((item: any) => String(item?.id) !== String(option.id));

    this.combosData[comboType].list = [option, ...listWithoutCurrentOption];
  }


  private setInitialSellerType(): void {
    const currentUserId = this.session.user?.id;

    if (this.sale?.user && currentUserId == this.sale.user.id && this.canUseMyselfSeller) {
      this.initialSellerType = 'myself';
      return;
    }

    if (this.sale?.user) {
      this.initialSellerType = 'system';
      return;
    }

    this.initialSellerType = 'guest';
  }

  private prependInitialOptionForCombo(comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' | 'guestUsers' | 'guestRetailers' | 'guestDistributionChannels'): void {

    // handle returning to initial load values
    switch (comboType) {
      case 'systemUsers':
        this.prependComboOption('systemUsers', this.initialSystemUserOption);
        break;
      case 'guestUsers':
        this.prependComboOption('guestUsers', this.initialGuestUserOption);
        break;
      case 'myselfRetailers':
        if (this.isInitialMyselfSeller()) {
          this.prependComboOption('myselfRetailers', this.initialRetailerOption);
        }
        break;
      case 'systemRetailers':
        if (this.isInitialSystemUserSelected()) {
          this.prependComboOption('systemRetailers', this.initialRetailerOption);
        }
        break;
      case 'guestRetailers':
        if (this.isInitialGuestUserSelected()) {
          this.prependComboOption('guestRetailers', this.initialRetailerOption);
        }
        break;
      case 'myselfDistributionChannels':
        if (this.isInitialRetailerSelected('myselfRetailerId')) {
          this.prependComboOption('myselfDistributionChannels', this.initialDistributionChannelOption);
        }
        break;
      case 'systemDistributionChannels':
        if (this.isInitialRetailerSelected('systemRetailerId')) {
          this.prependComboOption('systemDistributionChannels', this.initialDistributionChannelOption);
        }
        break;
      case 'guestDistributionChannels':
        if (this.isInitialRetailerSelected('guestRetailerId')) {
          this.prependComboOption('guestDistributionChannels', this.initialDistributionChannelOption);
        }
        break;
    }
  }

  private isInitialSellerType(sellerType: string): boolean {
    return this.initialSellerType === sellerType;
  }

  private async restoreInitialSellerValuesForType(sellerType: string): Promise<boolean> {
    if (!this.isInitialSellerType(sellerType)) {
      return false;
    }

    switch (sellerType) {
      case 'myself':
        if (!this.initialRetailerOption?.id) {
          return false;
        }

        this.prependComboOption('myselfRetailers', this.initialRetailerOption);

        this.runDropdownSync(() => {
          this.saleForm.controls['myselfRetailerId'].setValue([this.initialRetailerOption.id], { emitEvent: false });
        });

        await this.onMyselfRetailerIdChange([this.initialRetailerOption.id]);

        if (this.initialDistributionChannelOption?.id) {
          this.prependComboOption('myselfDistributionChannels', this.initialDistributionChannelOption);

          this.runDropdownSync(() => {
            this.saleForm.controls['myselfDistributionChannelId'].setValue([this.initialDistributionChannelOption.id], { emitEvent: false });
          });

          await this.onMyselfDistributionChannelIdChange([this.initialDistributionChannelOption.id]);
        }

        return true;

      case 'system':
        if (!this.initialSystemUserOption?.id) {
          return false;
        }

        this.prependComboOption('systemUsers', this.initialSystemUserOption);

        this.runDropdownSync(() => {
          this.saleForm.controls['systemUserId'].setValue([this.initialSystemUserOption.id], { emitEvent: false });
        });

        await this.onSystemUserIdChange([this.initialSystemUserOption.id]);

        if (this.initialRetailerOption?.id) {
          this.prependComboOption('systemRetailers', this.initialRetailerOption);

          this.runDropdownSync(() => {
            this.saleForm.controls['systemRetailerId'].setValue([this.initialRetailerOption.id], { emitEvent: false });
          });

          await this.onSystemRetailerIdChange([this.initialRetailerOption.id]);
        }

        if (this.initialDistributionChannelOption?.id) {
          this.prependComboOption('systemDistributionChannels', this.initialDistributionChannelOption);

          this.runDropdownSync(() => {
            this.saleForm.controls['systemDistributionChannelId'].setValue([this.initialDistributionChannelOption.id], { emitEvent: false });
          });

          await this.onSystemDistributionChannelIdChange([this.initialDistributionChannelOption.id]);
        }

        return true;

      case 'guest':
        if (!this.initialGuestUserOption?.id) {
          return false;
        }

        this.prependComboOption('guestUsers', this.initialGuestUserOption);

        this.runDropdownSync(() => {
          this.saleForm.controls['guestUserId'].setValue([this.initialGuestUserOption.id], { emitEvent: false });
        });

        await this.onGuestUserIdChange([this.initialGuestUserOption.id]);

        if (this.initialRetailerOption?.id) {
          this.prependComboOption('guestRetailers', this.initialRetailerOption);

          this.runDropdownSync(() => {
            this.saleForm.controls['guestRetailerId'].setValue([this.initialRetailerOption.id], { emitEvent: false });
          });

          await this.onGuestRetailerIdChange([this.initialRetailerOption.id]);
        }

        if (this.initialDistributionChannelOption?.id) {
          this.prependComboOption('guestDistributionChannels', this.initialDistributionChannelOption);

          this.runDropdownSync(() => {
            this.saleForm.controls['guestDistributionChannelId'].setValue([this.initialDistributionChannelOption.id], { emitEvent: false });
          });

          await this.onGuestDistributionChannelIdChange([this.initialDistributionChannelOption.id]);
        }

        return true;

      default:
        return false;
    }
  }

  private setInitialSellerOptions(): void {
    this.initialSystemUserOption = this.sale?.user
      ? {
        id: this.sale.user.id,
        text: `${this.sale.user.firstName ?? ''} ${this.sale.user.lastName ?? ''} (${this.sale.user.username ?? ''}) ${this.initialLoadCurrentOptionText}`.trim()
      }
      : null;

    this.initialGuestUserOption = this.sale?.unregisteredUser
      ? {
        id: this.sale.unregisteredUser.id,
        text: `${this.sale.unregisteredUser.firstName ?? ''} ${this.sale.unregisteredUser.lastName ?? ''} ${this.initialLoadCurrentOptionText}`.trim()
      }
      : null;

    this.initialRetailerOption = this.sale?.retailer
      ? {
        id: this.sale.retailer.id,
        text: `${this.sale.retailer.name ?? `Retailer #${this.sale.retailer.id}`} ${this.initialLoadCurrentOptionText}`
      }
      : null;

    this.initialDistributionChannelOption = this.sale?.distributionChannel
      ? {
        id: this.sale.distributionChannel.id,
        text: `${this.sale.distributionChannel.name ?? `Distribution Channel #${this.sale.distributionChannel.id}`} ${this.initialLoadCurrentOptionText}`
      }
      : null;
  }

  private isInitialMyselfSeller(): boolean {
    return this.session.user?.id == this.sale?.userId;
  }

  private isInitialSystemUserSelected(): boolean {
    return this.getSelectedId(this.saleForm.get('systemUserId')?.value) == this.sale?.userId;
  }

  private isInitialGuestUserSelected(): boolean {
    return this.getSelectedId(this.saleForm.get('guestUserId')?.value) == this.sale?.unregisteredUserId;
  }

  private isInitialRetailerSelected(controlName: string): boolean {
    return this.getSelectedId(this.saleForm.get(controlName)?.value) == this.sale?.retailerId;
  }

  private setSingleSelectControlValue(controlName: string, option: any | null) {
    this.runDropdownSync(() => {
      this.saleForm.get(controlName)?.setValue(option?.id != null ? [option.id] : [], { emitEvent: false });
    });
  }

  private async tryAutoSelectSingleComboOption(
    comboType: 'myselfRetailers' | 'myselfDistributionChannels' | 'systemUsers' | 'systemRetailers' | 'systemDistributionChannels' |'guestUsers' | 'guestRetailers' | 'guestDistributionChannels',
    controlName: string,
    handler: (value: any) => Promise<void>
  ): Promise<void> {

    const list = this.combosData[comboType].list ?? [];
    const totalRecords = this.combosData[comboType].totalRecords ?? list.length;

    if (totalRecords !== 1 || list.length !== 1) {
      return;
    }

    // if there's only one option -> autoselect it
    const selectedOption = list[0];
    this.setSingleSelectControlValue(controlName, selectedOption);

    await handler([selectedOption.id]);
  }

  private setRequired(controlName: string, required: boolean): void {
    const control = this.saleForm.get(controlName);
    if (!control) {
      return;
    }

    if (required) {
      control.setValidators([Validators.required]);
    } else {
      control.clearValidators();
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  private setSellerRetailerBusinessFormError(hasError: boolean): void {
    const currentErrors = this.saleForm.errors ?? {};

    if (hasError) {
      this.saleForm.setErrors({
        ...currentErrors,
        sellerRetailerBusinessError: true
      });

      return;
    }

    const { sellerRetailerBusinessError, ...remainingErrors } = currentErrors;
    this.saleForm.setErrors(Object.keys(remainingErrors).length ? remainingErrors : null);
  }

  private runDropdownSync(action: () => void) {
    this.isSyncingDropdowns = true; // guard to avoid broken init flow
    try {
      action();
    } finally {
      this.isSyncingDropdowns = false;
    }
  }

  private bindDropdownValueChanges() {
    this.saleForm.get('myselfRetailerId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onMyselfRetailerIdChange(value);
      });

    this.saleForm.get('myselfDistributionChannelId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onMyselfDistributionChannelIdChange(value);
      });

    this.saleForm.get('systemUserId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onSystemUserIdChange(value);
      });

    this.saleForm.get('systemRetailerId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onSystemRetailerIdChange(value);
      });

    this.saleForm.get('systemDistributionChannelId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onSystemDistributionChannelIdChange(value);
      });

    this.saleForm.get('guestUserId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onGuestUserIdChange(value);
      });

    this.saleForm.get('guestRetailerId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onGuestRetailerIdChange(value);
      });

    this.saleForm.get('guestDistributionChannelId')?.valueChanges
      .pipe(takeUntil(this.destroyedComponent$))
      .subscribe(value => {
        if (this.isSyncingDropdowns) return;
        this.onGuestDistributionChannelIdChange(value);
      });
  }

  private extractNumberArray(response: any, key?: string): number[] {
    // some API methods return a plain array, others may wrap it in `{ body }`, normalise it!
    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response?.body)) {
      return response.body;
    }

    if (key && Array.isArray(response?.[key])) {
      return response[key];
    }

    return [];
  }

  qrCodeScanned(data: any) {
    this.saleForm.controls['serialNumber'].setValue(data, { emitEvent: false });
  }

  async getCustomerData(customerId: any) {
    if (!customerId || !this.showCustomer) {
      return null;
    }

    try {
      const customer = await firstValueFrom(this.customersService.apiCustomersIdGet(customerId));
      return customer || null;
    } catch (error) {
      console.error('Error fetching customer data:', error);
      return null;
    }
  }

  onLazyLoadStores(event: any) {
    this.getStores(event);
  }

  async getStores(event: any) {
    const filters: any[] = [];

    if (event.filterData) {
      filters.push({
        propertyName: 'name',
        operator: this.filterOperator.CONTAINS,
        values: [event.filterData]
      });
    }

    filters.push({
      propertyName: 'isActive',
      operator: 'eq',
      values: ['true']
    });

    const filter = {
      pageNumber: event.activePage,
      pageSize: this.storesRowsPerPageOption,
      filters
    };

    const storesPage = await firstValueFrom(this.retailersService.apiRetailersFilterComboPost(filter));
    this.storesList = storesPage.results;
    this.storesTotalRecords = storesPage.totalCount;
  }

  onLazyLoadUsers(event: any) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.getUsers(event);
    }, 300);
  }

  async getUsers(event: any) {
    const payload : FilterRequestForCombo = {
      pageNumber: event.activePage,
      pageSize: this.usersRowsPerPageOption
    }
    if (event.filterData) {
      payload.textFilter = event.filterData;
    }

    const usersPage = await firstValueFrom(this.usersService.apiUsersFilterComboPost(payload));
    this.usersList = usersPage.results;
    this.usersTotalRecords = usersPage.totalCount;
  }

  onLazyLoadCustomers(event: any) {
    this.getCustomers(event);
  }

  async getCustomers(event: any) {
    if (this.showCustomer && this.canAssignCustomer) {
      const search = event?.search?.trim() || null;
      const activePage = Math.max(1, (event?.pageIndex ?? 1));
      const payload : FilterRequestForCombo = {
        pageNumber: activePage,
        pageSize: event?.pageSize ?? this.customersRowsPerPageOption
      }

      const filters: any[] = [];
      const response = await firstValueFrom(this.retailersService.apiRetailersIdCustomerIdsGet(Number(this.sale?.retailerId)));
      const customerIds = response.customerIds ?? [];

      filters.push({
        propertyName: 'Id',
        operator: this.filterOperator.IN,
        values: customerIds.map((id: number) => id.toString())
      });


      if (search) {
        payload.textFilter = search
      }
      payload.additionalFilters = filters;

      const customersPage = await firstValueFrom(this.customersService.apiCustomersFilterComboPost(payload));
      this.customersList = (customersPage.results ?? []).map((customer: any) => ({
        ...customer,
        fullName: `${customer.firstName ?? ''} ${customer.lastName ?? ''}`.trim()
      }));
      this.customersTotalRecords = customersPage.totalCount ?? 0;
    }
  }

  onLazyLoadDistributionChannel(event: any) {
    this.getDistributionChannels();
  }

  getDistributionChannels() {
    this.distributionChannelsList = this.saleForm.controls['retailer'].value?.distributionChannels ?? [];
    this.distributionChannelsTotalRecords = this.distributionChannelsList.length;
  }

  onLazyLoadSellers(event: any) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.getSellers(event);
    }, 300);
  }

  async getSellers(event: any) {
    const payload : FilterRequestForCombo = {
      pageNumber: event.activePage,
      pageSize: this.sellersRowsPerPageOption
    }
    if (event.filterData) {
      payload.textFilter = event.filterData;
    }

    const sellersPage = await firstValueFrom(this.sellersService.apiUnregisteredUsersFilterComboPost(payload));
    this.sellersList = sellersPage.results;
    this.sellersTotalRecords = sellersPage.totalCount;
  }

  async initForm() {
    const selectedCustomer = await this.getSaleCustomer();
    this.ensureCustomerOption(selectedCustomer);

    this.saleForm.controls['item'].setValue(this.sale?.item?.product?.name);
    this.saleForm.controls['itemId'].setValue(this.sale?.itemId);
    this.saleForm.controls['serialNumber'].setValue(this.sale?.item?.serialNumber);
    this.saleForm.controls['distributionChannel'].setValue(this.sale?.distributionChannel);
    this.saleForm.controls['isLegacy'].setValue(this.sale?.isLegacy ? 'true' : 'false');
    this.saleForm.controls['isGift'].setValue(this.sale?.isGift ? 'true' : 'false');
    this.saleForm.controls['price'].setValue(this.sale?.price);
    this.saleForm.controls['date'].setValue(new Date(this.sale?.date ?? ''));
    this.saleForm.controls['customer'].setValue(selectedCustomer);
    this.saleForm.controls['customerId'].setValue(this.sale?.customerId ? [this.sale.customerId] : []);
    this.saleForm.controls['retailer'].setValue(this.sale?.retailer);
    this.saleForm.controls['retailerId'].setValue(this.sale?.retailerId);
    this.saleForm.controls['userId'].setValue(this.sale?.userId);
    this.saleForm.controls['user'].setValue(this.sale?.user);
    this.saleForm.controls['unregisteredUserId'].setValue(this.sale?.unregisteredUserId);
    this.saleForm.controls['unregisteredUser'].setValue(this.sale?.unregisteredUser);

    this.isSystemSeller = this.sale?.user;
    this.isGuestSeller = this.sale?.unregisteredUser;
    const currentUserId = this.session.user?.id;

    if (this.isSystemSeller != null) {
      if (currentUserId == this.isSystemSeller.id && this.canUseMyselfSeller) {
        this.saleForm.controls['sellerType'].setValue('myself');

        await this.combosData['myselfRetailers'].getData(null, 1);
        this.prependInitialOptionForCombo('myselfRetailers');

        const retailer = this.initialRetailerOption ?? (this.sale?.retailer
          ? { id: this.sale.retailer.id, text: this.sale.retailer.name }
          : null);

        const distributionChannel = this.initialDistributionChannelOption ?? (this.sale?.distributionChannel
          ? {
            id: this.sale.distributionChannel.id,
            text: this.sale.distributionChannel.name ?? `Distribution Channel #${this.sale.distributionChannel.id}`
          }
          : null);

        this.prependComboOption('myselfRetailers', retailer);
        this.runDropdownSync(() => {
          this.saleForm.controls['myselfRetailerId'].setValue(retailer ? [retailer.id] : [], { emitEvent: false });
        });
        await this.onMyselfRetailerIdChange(retailer ? [retailer.id] : []);

        this.prependComboOption('myselfDistributionChannels', distributionChannel);
        this.runDropdownSync(() => {
          this.saleForm.controls['myselfDistributionChannelId'].setValue(distributionChannel ? [distributionChannel.id] : [], { emitEvent: false });
        });
        await this.onMyselfDistributionChannelIdChange(distributionChannel ? [distributionChannel.id] : []);
      } else {
        this.saleForm.controls['sellerType'].setValue('system');
        await this.onSellerTypeChange({ value: 'system' });

        const user = this.initialSystemUserOption ?? (this.sale?.user
          ? {
            id: this.sale.user.id,
            text: `${this.sale.user.firstName} ${this.sale.user.lastName} (${this.sale.user.username})`
          }
          : null);

        const retailer = this.initialRetailerOption ?? (this.sale?.retailer
          ? { id: this.sale.retailer.id, text: this.sale.retailer.name }
          : null);

        const distributionChannel = this.initialDistributionChannelOption ?? (this.sale?.distributionChannel
          ? {
            id: this.sale.distributionChannel.id,
            text: this.sale.distributionChannel.name ?? `Distribution Channel #${this.sale.distributionChannel.id}`
          }
          : null);

        this.ensureComboOption('systemUsers', user);
        this.runDropdownSync(() => {
          this.saleForm.controls['systemUserId'].setValue(user ? [user.id] : [], { emitEvent: false });
        });
        await this.onSystemUserIdChange(user ? [user.id] : []);

        this.prependComboOption('systemRetailers', retailer);
        this.runDropdownSync(() => {
          this.saleForm.controls['systemRetailerId'].setValue(retailer ? [retailer.id] : [], { emitEvent: false });
        });
        await this.onSystemRetailerIdChange(retailer ? [retailer.id] : []);

        this.prependComboOption('systemDistributionChannels', distributionChannel);
        this.runDropdownSync(() => {
          this.saleForm.controls['systemDistributionChannelId'].setValue(distributionChannel ? [distributionChannel.id] : [], { emitEvent: false });
        });
        await this.onSystemDistributionChannelIdChange(distributionChannel ? [distributionChannel.id] : []);
      }
    } else {
      await this.setGuestSeller();
    }
  }

  async setGuestSeller() {
    this.saleForm.controls['sellerType'].setValue('guest');
    await this.onSellerTypeChange({ value: 'guest' });
    this.unregisteredUserName = `${this.sale?.unregisteredUser?.firstName ?? ''} ${this.sale?.unregisteredUser?.lastName ?? ''}`;

    const unregisteredUser = this.initialGuestUserOption ?? (this.sale?.unregisteredUser
      ? {
        id: this.sale.unregisteredUser.id,
        text: `${this.sale.unregisteredUser.firstName} ${this.sale.unregisteredUser.lastName}`
      }
      : null);

    const retailer = this.initialRetailerOption ?? (this.sale?.retailer
      ? { id: this.sale.retailer.id, text: this.sale.retailer.name }
      : null);

    const distributionChannel = this.initialDistributionChannelOption ?? (this.sale?.distributionChannel
      ? {
        id: this.sale.distributionChannel.id,
        text: this.sale.distributionChannel.name ?? `Distribution Channel #${this.sale.distributionChannel.id}`
      }
      : null);

    this.ensureComboOption('guestUsers', unregisteredUser);
    this.runDropdownSync(() => {
      this.saleForm.controls['guestUserId'].setValue(unregisteredUser ? [unregisteredUser.id] : [], { emitEvent: false });
    });
    await this.onGuestUserIdChange(unregisteredUser ? [unregisteredUser.id] : []);

    this.prependComboOption('guestRetailers', retailer);
    this.runDropdownSync(() => {
      this.saleForm.controls['guestRetailerId'].setValue(retailer ? [retailer.id] : [], { emitEvent: false });
    });
    await this.onGuestRetailerIdChange(retailer ? [retailer.id] : []);

    this.prependComboOption('guestDistributionChannels', distributionChannel);
    this.runDropdownSync(() => {
      this.saleForm.controls['guestDistributionChannelId'].setValue(distributionChannel ? [distributionChannel.id] : [], { emitEvent: false });
    });
    await this.onGuestDistributionChannelIdChange(distributionChannel ? [distributionChannel.id] : []);
  }

  private updateCustomerControlState() {
    const control = this.saleForm.get('customerId');
    if (!control) {
      return;
    }

    if (this.canAssignCustomer) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  async submitForm() {
    if (this.saleForm.valid) {
      const dateObj = new Date(this.saleForm.controls['date'].value);
      const utcDate = dateObj.toISOString();

      const sellerType = this.saleForm.controls['sellerType'].value;
      const fallbackUserId = this.session.user?.id;

      let sellerPayload: Partial<SaleRequest> = {};

      if (sellerType === 'myself') {
        sellerPayload = {
          userId: this.saleForm.controls['user'].value?.id ?? fallbackUserId
        };
      } else if (sellerType === 'system') {
        sellerPayload = {
          userId: this.saleForm.controls['user'].value?.id ?? fallbackUserId
        };
      } else if (sellerType === 'guest') {
        sellerPayload = {
          unregisteredUserId: this.saleForm.controls['unregisteredUser'].value?.id
        };
      }

      const payload: SaleRequest = {
        itemId: this.saleForm.controls['itemId'].value,
        retailerId: this.saleForm.controls['retailer'].value?.id,
        distributionChannelId: this.saleForm.controls['distributionChannel'].value?.id,
        isLegacy: this.saleForm.controls['isLegacy'].value === 'true',
        isGift: this.saleForm.controls['isGift'].value === 'true',
        date: utcDate,
        price: this.saleForm.controls['price'].value,
        customerId: this.saleForm.controls['customerId'].value?.[0],
        ...sellerPayload
      };

      await firstValueFrom(this.salesService.apiSalesUpdateIdPut(this.saleId, payload));
      this.router.navigate(['/sales', this.saleId]);
    }
  }

  async onRetailerChange() {
    await this.getCanModifyPrice();

    await this.getCanAssignCustomer();
    this.updateCustomerControlState();

    await this.getCustomers({ pageIndex: 1, pageSize: this.customersRowsPerPageOption, search: '' });
    this.getDistributionChannels();

    if (!this.canAssignCustomer) {
      const selectedCustomer = await this.getSaleCustomer();
      this.ensureCustomerOption(selectedCustomer);

      this.saleForm.patchValue({
        customerId: this.sale?.customerId ? [this.sale.customerId] : []
      });
    }

    if (!this.canModifyPrice) {
      this.saleForm.patchValue({
        price: this.sale?.price
      });
    }

    if (this.saleForm.controls['retailer'].value?.id === this.sale.retailerId) {
      this.saleForm.patchValue({
        distributionChannel: this.sale?.distributionChannel
      });
    }
  }

  async getCanModifyPrice() {
    this.canModifyPrice = (await firstValueFrom(this.salesService.apiSalesCanModifyPriceRetailerIdGet(this.saleForm.controls['retailer'].value?.id))).response ?? false;
  }

  async getCanAssignCustomer() {
    this.canAssignCustomer = (await firstValueFrom(this.salesService.apiSalesCanAssignCustomerRetailerIdGet(this.saleForm.controls['retailer'].value?.id))).response ?? false;
  }

  // Myself flow: selecting a retailer determines the available distribution channels
  async onMyselfRetailerIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    this.runDropdownSync(() => {
      this.saleForm.get('myselfDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
    });

    this.combosData['myselfDistributionChannels'].reset();

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('myselfRetailerId')?.reset([], { emitEvent: false });
        this.saleForm.get('retailer')?.reset(null, { emitEvent: false });
      });

      return;
    }

    const selectedOption = this.findComboOption('myselfRetailers', selectedId);
    this.saleForm.controls['retailer'].setValue(selectedOption);

    if (!this.showDistributionChannel) {
      return;
    }

    const distributionChannelsResponse = await firstValueFrom(
      this.retailersService.apiRetailersIdDistributionChannelIdsGet(Number(selectedId))
    );
    const distributionChannelsIds = this.extractNumberArray(distributionChannelsResponse, 'distributionChannelIds');

    this.combosData['myselfDistributionChannels'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: distributionChannelsIds.map((id: number) => id.toString())
      }
    ];

    await this.combosData['myselfDistributionChannels'].getData(null, 1);
    this.prependInitialOptionForCombo('myselfDistributionChannels');

    await this.tryAutoSelectSingleComboOption('myselfDistributionChannels', 'myselfDistributionChannelId', this.onMyselfDistributionChannelIdChange.bind(this));
  }

  async onMyselfDistributionChannelIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('myselfDistributionChannelId')?.reset([], { emitEvent: false });
        this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
      });
      return;
    }

    const selectedOption = this.findComboOption('myselfDistributionChannels', selectedId);
    this.saleForm.controls['distributionChannel'].setValue(selectedOption);
  }

  // System flow: selecting a user constrains the retailer list to retailers assigned to that user
  async onSystemUserIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    this.runDropdownSync(() => {
      this.saleForm.get('systemRetailerId')?.reset([], { emitEvent: false });
      this.saleForm.get('systemDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('retailer')?.reset(null, { emitEvent: false });
      this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
    });

    this.combosData['systemRetailers'].reset();
    this.combosData['systemDistributionChannels'].reset();
    this.showErrorSystemNoRetailersAssigned = false;
    this.setSellerRetailerBusinessFormError(false);

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('systemUserId')?.reset([], { emitEvent: false });
        this.saleForm.get('user')?.reset(null, { emitEvent: false });
      });

      return;
    }

    const selectedOption = this.findComboOption('systemUsers', selectedId);

    const retailersResponse = await firstValueFrom(
      this.usersService.apiUsersIdRetailerIdsGet(String(selectedId))
    );
    const retailersIds = this.extractNumberArray(retailersResponse, 'retailerIds');

    this.combosData['systemRetailers'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: retailersIds.map((id: number) => id.toString())
      }
    ];

    this.saleForm.controls['user'].setValue(selectedOption);

    await this.combosData['systemRetailers'].getData(null, 1);
    this.prependInitialOptionForCombo('systemRetailers');

    const hasInitialRetailerOption = this.isInitialSystemUserSelected() && !!this.initialRetailerOption?.id;
    this.showErrorSystemNoRetailersAssigned = retailersIds.length === 0 && !hasInitialRetailerOption;

    this.setRequired('systemRetailerId', !this.showErrorSystemNoRetailersAssigned);
    this.setRequired('systemDistributionChannelId', !this.showErrorSystemNoRetailersAssigned && this.showDistributionChannel);
    this.setSellerRetailerBusinessFormError(this.showErrorSystemNoRetailersAssigned);

    if (this.showErrorSystemNoRetailersAssigned) {
      this.saleForm.get('systemRetailerId')?.markAsUntouched();
      this.saleForm.get('systemDistributionChannelId')?.markAsUntouched();
      return;
    }

    await this.tryAutoSelectSingleComboOption('systemRetailers', 'systemRetailerId',this.onSystemRetailerIdChange.bind(this));
  }

  // System flow: selecting a retailer constrains the distribution channels available for that retailer
  async onSystemRetailerIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    this.runDropdownSync(() => {
      this.saleForm.get('systemDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
    });

    this.combosData['systemDistributionChannels'].reset();

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('systemRetailerId')?.reset([], { emitEvent: false });
        this.saleForm.get('retailer')?.reset(null, { emitEvent: false });
      });

      return;
    }

    const selectedOption = this.findComboOption('systemRetailers', selectedId);

    this.saleForm.controls['retailer'].setValue(selectedOption);

    if (!this.showDistributionChannel) {
      return;
    }

    const distributionChannelsResponse = await firstValueFrom(
      this.retailersService.apiRetailersIdDistributionChannelIdsGet(Number(selectedId))
    );
    const distributionChannelsIds = this.extractNumberArray(distributionChannelsResponse, 'distributionChannelIds');

    this.combosData['systemDistributionChannels'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: distributionChannelsIds.map((id: number) => id.toString())
      }
    ];

    await this.combosData['systemDistributionChannels'].getData(null, 1);
    this.prependInitialOptionForCombo('systemDistributionChannels');

    await this.tryAutoSelectSingleComboOption('systemDistributionChannels', 'systemDistributionChannelId', this.onSystemDistributionChannelIdChange.bind(this));
  }

  async onSystemDistributionChannelIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('systemDistributionChannelId')?.reset([], { emitEvent: false });
        this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
      });
      return;
    }

    const selectedOption = this.findComboOption('systemDistributionChannels', selectedId);
    this.saleForm.controls['distributionChannel'].setValue(selectedOption);
  }

  // Guest flow: selecting an unregistered user constrains the retailer list assigned to that seller
  async onGuestUserIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    this.runDropdownSync(() => {
      this.saleForm.get('guestRetailerId')?.reset([], { emitEvent: false });
      this.saleForm.get('guestDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('retailer')?.reset(null, { emitEvent: false });
      this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
    });

    this.combosData['guestRetailers'].reset();
    this.combosData['guestDistributionChannels'].reset();
    this.showErrorGuestNoRetailersAssigned = false;
    this.setSellerRetailerBusinessFormError(false);

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('guestUserId')?.reset([], { emitEvent: false });
        this.saleForm.get('unregisteredUser')?.reset(null, { emitEvent: false });
      });

      return;
    }

    const selectedOption = this.findComboOption('guestUsers', selectedId);

    const retailersResponse = await firstValueFrom(
      this.sellersService.apiUnregisteredUsersIdRetailerIdsGet(Number(selectedId))
    );
    const retailersIds = this.extractNumberArray(retailersResponse, 'retailerIds');

    this.combosData['guestRetailers'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: retailersIds.map((id: number) => id.toString())
      }
    ];

    this.saleForm.controls['unregisteredUser'].setValue(selectedOption);

    await this.combosData['guestRetailers'].getData(null, 1);
    this.prependInitialOptionForCombo('guestRetailers');

    const hasInitialRetailerOption = this.isInitialGuestUserSelected() && !!this.initialRetailerOption?.id;
    this.showErrorGuestNoRetailersAssigned = retailersIds.length === 0 && !hasInitialRetailerOption;

    this.setRequired('guestRetailerId', !this.showErrorGuestNoRetailersAssigned);
    this.setRequired('guestDistributionChannelId', !this.showErrorGuestNoRetailersAssigned && this.showDistributionChannel);
    this.setSellerRetailerBusinessFormError(this.showErrorGuestNoRetailersAssigned);

    if (this.showErrorGuestNoRetailersAssigned) {
      this.saleForm.get('guestRetailerId')?.markAsUntouched();
      this.saleForm.get('guestDistributionChannelId')?.markAsUntouched();
      return;
    }

    await this.tryAutoSelectSingleComboOption('guestRetailers', 'guestRetailerId', this.onGuestRetailerIdChange.bind(this));
  }

  // Guest flow: selecting a retailer constrains the distribution channels available for that retailer
  async onGuestRetailerIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    this.runDropdownSync(() => {
      this.saleForm.get('guestDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
    });

    this.combosData['guestDistributionChannels'].reset();

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('guestRetailerId')?.reset([], { emitEvent: false });
        this.saleForm.get('retailer')?.reset(null, { emitEvent: false });
      });

      return;
    }

    const selectedOption = this.findComboOption('guestRetailers', selectedId);

    this.saleForm.controls['retailer'].setValue(selectedOption);

    if (!this.showDistributionChannel) {
      return;
    }

    const distributionChannelsResponse = await firstValueFrom(
      this.retailersService.apiRetailersIdDistributionChannelIdsGet(Number(selectedId))
    );
    const distributionChannelsIds = this.extractNumberArray(distributionChannelsResponse, 'distributionChannelIds');

    this.combosData['guestDistributionChannels'].additionalFilters = [
      {
        propertyName: 'Id',
        operator: FilterOperator.IN,
        values: distributionChannelsIds.map((id: number) => id.toString())
      }
    ];

    await this.combosData['guestDistributionChannels'].getData(null, 1);
    this.prependInitialOptionForCombo('guestDistributionChannels');

    await this.tryAutoSelectSingleComboOption('guestDistributionChannels', 'guestDistributionChannelId', this.onGuestDistributionChannelIdChange.bind(this));
  }

  async onGuestDistributionChannelIdChange(value: any) {
    const selectedId = this.getSelectedId(value);

    if (!selectedId) {
      this.runDropdownSync(() => {
        this.saleForm.get('guestDistributionChannelId')?.reset([], { emitEvent: false });
        this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
      });
      return;
    }

    const selectedOption = this.findComboOption('guestDistributionChannels', selectedId);
    this.saleForm.controls['distributionChannel'].setValue(selectedOption);
  }

  async onSellerTypeChange(event: any) {
    this.restartForm();

    this.showErrorSystemNoRetailersAssigned = false;
    this.showErrorGuestNoRetailersAssigned = false;
    this.setSellerRetailerBusinessFormError(false);

    this.toggleMandatoryFieldsValidation(event.value);

    switch (event.value) {
      case 'myself':
        await this.combosData['myselfRetailers'].getData(null, 1);
        this.prependInitialOptionForCombo('myselfRetailers');
        this.saleForm.controls['user'].setValue(this.session.user);
        this.isSystemSeller = true;
        this.isGuestSeller = false;

        if (!(await this.restoreInitialSellerValuesForType('myself'))) {
          await this.tryAutoSelectSingleComboOption('myselfRetailers', 'myselfRetailerId', this.onMyselfRetailerIdChange.bind(this));
        }
        break;

      case 'system':
        await this.combosData['systemUsers'].getData(null, 1);
        this.prependInitialOptionForCombo('systemUsers');
        this.isSystemSeller = true;
        this.isGuestSeller = false;

        if (!(await this.restoreInitialSellerValuesForType('system'))) {
          await this.tryAutoSelectSingleComboOption('systemUsers', 'systemUserId', this.onSystemUserIdChange.bind(this));
        }
        break;

      case 'guest':
        await this.combosData['guestUsers'].getData(null, 1);
        this.prependInitialOptionForCombo('guestUsers');
        this.isSystemSeller = false;
        this.isGuestSeller = true;

        if (!(await this.restoreInitialSellerValuesForType('guest'))) {
          await this.tryAutoSelectSingleComboOption('guestUsers', 'guestUserId', this.onGuestUserIdChange.bind(this));
        }
        break;
    }
  }

  async onCreateGuestUserChange(event: any) {
    this.restartForm();

    if (event.checked === true) {
      await this.combosData['createGuestRetailers']?.getData(null, 1);
    } else {
      await this.combosData['guestUsers'].getData(null, 1);
      this.prependInitialOptionForCombo('guestUsers');
    }

    this.toggleMandatoryFieldsValidation('guest');
  }

  restartForm() {
    this.runDropdownSync(() => {
      // Clear controls without retriggering the dropdown cascade
      this.saleForm.get('myselfRetailerId')?.reset([], { emitEvent: false });
      this.saleForm.get('myselfDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('systemUserId')?.reset([], { emitEvent: false });
      this.saleForm.get('systemRetailerId')?.reset([], { emitEvent: false });
      this.saleForm.get('systemDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('guestUserId')?.reset([], { emitEvent: false });
      this.saleForm.get('guestRetailerId')?.reset([], { emitEvent: false });
      this.saleForm.get('guestDistributionChannelId')?.reset([], { emitEvent: false });
      this.saleForm.get('firstname')?.reset(null, { emitEvent: false });
      this.saleForm.get('lastname')?.reset(null, { emitEvent: false });
      this.saleForm.get('createGuestRetailerId')?.reset(null, { emitEvent: false });
      this.saleForm.get('retailer')?.reset(null, { emitEvent: false });
      this.saleForm.get('distributionChannel')?.reset(null, { emitEvent: false });
      this.saleForm.get('user')?.reset(null, { emitEvent: false });
      this.saleForm.get('unregisteredUser')?.reset(null, { emitEvent: false });
    });

    Object.keys(this.combosData).forEach(key => {
      this.combosData[key].reset();
    });
  }

  toggleMandatoryFieldsValidation(value: string) {
    const myselfRetailerIdControl = this.saleForm.get('myselfRetailerId');
    const myselfDistributionChannelIdControl = this.saleForm.get('myselfDistributionChannelId');

    const systemUserIdControl = this.saleForm.get('systemUserId');
    const systemRetailerIdControl = this.saleForm.get('systemRetailerId');
    const systemDistributionChannelIdControl = this.saleForm.get('systemDistributionChannelId');

    const guestUserIdControl = this.saleForm.get('guestUserId');
    const guestRetailerIdControl = this.saleForm.get('guestRetailerId');
    const guestDistributionChannelIdControl = this.saleForm.get('guestDistributionChannelId');
    const firstnameControl = this.saleForm.get('firstname');
    const lastnameControl = this.saleForm.get('lastname');

    myselfRetailerIdControl?.clearValidators();
    myselfDistributionChannelIdControl?.clearValidators();
    systemUserIdControl?.clearValidators();
    systemRetailerIdControl?.clearValidators();
    systemDistributionChannelIdControl?.clearValidators();
    guestUserIdControl?.clearValidators();
    guestRetailerIdControl?.clearValidators();
    guestDistributionChannelIdControl?.clearValidators();
    firstnameControl?.clearValidators();
    lastnameControl?.clearValidators();

    switch (value) {
      case 'myself':
        myselfRetailerIdControl?.setValidators([Validators.required]);
        if(this.showDistributionChannel){
           myselfDistributionChannelIdControl?.setValidators([Validators.required]);
        }
        break;
      case 'system':
        systemUserIdControl?.setValidators([Validators.required]);
        systemRetailerIdControl?.setValidators([Validators.required]);
        if(this.showDistributionChannel){
          systemDistributionChannelIdControl?.setValidators([Validators.required]);
        }
        break;
      case 'guest':
        guestUserIdControl?.setValidators([Validators.required]);
        guestRetailerIdControl?.setValidators([Validators.required]);
        if(this.showDistributionChannel){
          guestDistributionChannelIdControl?.setValidators([Validators.required]);
        }
        break;
    }

    myselfRetailerIdControl?.updateValueAndValidity();
    myselfDistributionChannelIdControl?.updateValueAndValidity();
    systemUserIdControl?.updateValueAndValidity();
    systemRetailerIdControl?.updateValueAndValidity();
    systemDistributionChannelIdControl?.updateValueAndValidity();
    guestUserIdControl?.updateValueAndValidity();
    guestRetailerIdControl?.updateValueAndValidity();
    guestDistributionChannelIdControl?.updateValueAndValidity();
    firstnameControl?.updateValueAndValidity();
    lastnameControl?.updateValueAndValidity();
  }

  private async getSaleCustomer() {
    // prefer `customerId` as `sale.customer.id` may not come in the resp
    const customerId = this.sale?.customerId ?? this.sale?.customer?.id;

    if (!customerId) {
      return null;
    }

    const customerData = await this.getCustomerData(customerId);

    if (!customerData) {
      return null;
    }

    return {
      ...customerData,
      fullName: `${customerData.firstName ?? ''} ${customerData.lastName ?? ''}`.trim()
    };
  }

  private ensureCustomerOption(customer: any | null) {

    if (!customer?.id) {
      return;
    }

    // ensure customer can be shown in the dropdown even if not part of the current customers page
    const exists = (this.customersList ?? []).some((x: any) => x?.id === customer.id);
    if (!exists) {
      this.customersList = [customer, ...(this.customersList ?? [])];
    }
  }

  updateCanUseMyselfSeller(): void {
    const userDetails = this.session.getUserDetails();

    const companyId = userDetails?.company;
    const currentCompanyId = userDetails?.currentCompany || this.session.getCompany();

    this.canUseMyselfSeller =
      !companyId ||
      !currentCompanyId ||
      String(companyId) === String(currentCompanyId);
  }

  ngOnDestroy(): void {
    this.destroyedComponent$.next();
    this.destroyedComponent$.complete();
  }
}
